// ============================================================================
// AREA FORM MODAL — Add / Edit Area (MSTR-AREA-001)
// ============================================================================
// Region dropdown labels include the parent zone for context (hierarchy clarity).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { areaService } from 'api/services/areaService';
import type { Area } from 'api/types/area.types';

type AreaStatus = 'ACTIVE' | 'INACTIVE';

interface AreaFormData {
  regionId: string;
  areaCode: string;
  areaName: string;
  statusCode: AreaStatus;
}

const INITIAL_FORM_DATA: AreaFormData = {
  regionId: '',
  areaCode: '',
  areaName: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface RegionOption {
  value: string;  // regionId
  label: string;  // e.g. "NORTH-DELHI — Delhi NCR Region · North Zone"
}

export interface AreaFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Area | null;
  /** Active regions for the dropdown — passed from parent screen. */
  regionOptions: RegionOption[];
  /** Seed options — used when editing an area whose current parent region is
   * no longer in the active list (INACTIVE / DELETED in another tab). */
  seedRegionOptions?: RegionOption[];
  onSaved: () => void;
  onClose: () => void;

  // Minimize support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const AreaFormModal: React.FC<AreaFormModalProps> = ({
  isOpen, mode, record, regionOptions, seedRegionOptions, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // Merge seed options into the active regions list so inactive/deleted parents render.
  const combinedRegionOptions = useMemo(() => {
    if (!seedRegionOptions || seedRegionOptions.length === 0) return regionOptions;
    const seedIds = new Set(seedRegionOptions.map(s => s.value));
    return [
      ...regionOptions.filter(o => !seedIds.has(o.value)),
      ...seedRegionOptions,
    ];
  }, [regionOptions, seedRegionOptions]);

  // Form state (Pattern B)
  const [formData, setFormData] = useState<AreaFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  // Minimize-state-capture refs
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  // Validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Populate / reset on open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: AreaFormData = rec
      ? {
          regionId: rec.regionId,
          areaCode: rec.areaCode,
          areaName: rec.areaName,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as AreaStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  // Minimize handler
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.areaCode || currentFormData.areaCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.areaCode?.trim();
      const name = currentFormData.areaName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.areaCode || currentRecord?.areaCode || 'Area'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.areaCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // Validate and open save confirmation
  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.areaCode.trim();
    const name = formData.areaName.trim();

    if (!formData.regionId) errors.regionId = 'Region is required';

    if (!code) errors.areaCode = 'Area code is required';
    else if (code.length > 20) errors.areaCode = 'Max 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.areaCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.areaName = 'Area name is required';
    else if (name.length > 100) errors.areaName = 'Max 100 characters';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // Confirmed save
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.areaCode.trim().toUpperCase();
      const name = formData.areaName.trim();

      if (mode === 'create' || mode === 'copy') {
        await areaService.create({
          regionId: formData.regionId,
          areaCode: code,
          areaName: name,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Area "${code}" created successfully.`);
      } else if (record) {
        await areaService.update(record.id, {
          regionId: formData.regionId,
          areaCode: code,
          areaName: name,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Area "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This area was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'An area with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save area. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Area' : mode === 'copy' ? 'Copy Area' : mode === 'create' ? 'Add Area' : 'Edit Area'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          {/* Region (parent FK). Labels include the parent zone for hierarchy context. */}
          <div className="form-group" data-field="regionId">
            <SyutSelect
              label={<>Region <span className="text-danger">*</span></>}
              required
              options={combinedRegionOptions}
              value={formData.regionId}
              onChange={(val) => {
                clearFieldError('regionId');
                setFormData(prev => ({ ...prev, regionId: (val as string) || '' }));
              }}
              error={getFieldError('regionId')}
              isClearable={false}
              placeholder="Select a region..."
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>

          {/* Area Code */}
          <div className="form-group" data-field="areaCode">
            <label className="form-label">
              Area Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('areaCode') ? ' form-field-error' : ''}`}
              value={formData.areaCode}
              maxLength={20}
              placeholder="DEL-C, KA-BLR-URB..."
              onChange={(e) => {
                clearFieldError('areaCode');
                setFormData(prev => ({ ...prev, areaCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('areaCode') && (
              <div className="form-error-message">{getFieldError('areaCode')}</div>
            )}
          </div>

          {/* Area Name */}
          <div className="form-group" data-field="areaName">
            <label className="form-label">
              Area Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('areaName') ? ' form-field-error' : ''}`}
              value={formData.areaName}
              maxLength={100}
              placeholder="Bengaluru Urban"
              onChange={(e) => {
                clearFieldError('areaName');
                setFormData(prev => ({ ...prev, areaName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('areaName') && (
              <div className="form-error-message">{getFieldError('areaName')}</div>
            )}
          </div>

          {/* Status */}
          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as AreaStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create area "${formData.areaCode.trim().toUpperCase()}"?`
            : `Update area "${formData.areaCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default AreaFormModal;
